import React, { useState, useEffect } from 'react';

import PropTypes from 'prop-types';
import { Embed, Form } from 'semantic-ui-react';

import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';

import HelpTooltip from 'components/common/HelpTooltip';
import { FileSelection } from 'components/modules/files/components';
import { BusinessFilePropType } from 'components/modules/files/types/files';
import useDebounce from 'hooks/useDebounce';

import { VideoPlayerSourcePropType } from './VideoPlayerEditor.types';
import {
  getVideoSourceOptions,
  getUrlLabel,
  calculateUrlAndId,
  calculateEmbedParams,
  getSourceFromUrl,
  validateVideoURL,
  getVideoUrlValidationMessage,
} from './VideoPlayerEditor.utils';
import VideoPlayerSources from './components/VideoPlayerSources';

import './VideoPlayerEditor.scss';

const propTypes = {
  videoSources: PropTypes.arrayOf(VideoPlayerSourcePropType),
  source: VideoPlayerSourcePropType,
  url: PropTypes.string,
  videoId: PropTypes.string,
  placeholderFile: BusinessFilePropType,
  onChange: PropTypes.func,
};

const defaultProps = {
  videoSources: [],
  source: undefined,
  url: '',
  videoId: '',
  placeholderFile: null,
  onChange: () => {},
};

export default function VideoPlayerEditor({
  videoSources,
  source,
  url,
  videoId,
  onChange,
  placeholderFile,
}) {
  const [isValidUrl, setIsValidUrl] = useState(true);
  const [urlErrorMessage, setUrlErrorMessage] = useState('');
  const [urlOrId, setUrlOrId] = useState(videoId || url);
  const videoSourceOptions = getVideoSourceOptions(videoSources);
  const urlLabel = getUrlLabel(source);
  const debouncedUrlOrId = useDebounce(urlOrId, 300);

  function validateUrl(newUrl) {
    if (_isNil(newUrl) || _isEmpty(newUrl)) {
      setIsValidUrl(true);
      setUrlErrorMessage('');
      return;
    }

    const sourceFromUrl = getSourceFromUrl(newUrl);
    const isValidSourceUrl = validateVideoURL(newUrl, source);
    const isSameSource = sourceFromUrl === source;
    const isValidNewUrl = isSameSource && isValidSourceUrl;

    setIsValidUrl(isValidNewUrl);
    if (isValidNewUrl) {
      setUrlErrorMessage('');
      return;
    }
    const validationMessage = getVideoUrlValidationMessage(isSameSource, isValidSourceUrl, source);
    setUrlErrorMessage(validationMessage);
  }

  useEffect(() => {
    validateUrl(url);
  }, [source, url]);

  useEffect(() => {
    setUrlOrId(videoId || url);
  }, [videoId, url]);

  function onChangeUrlOrId(event, { value }) {
    const changes = calculateUrlAndId(source, value);
    validateUrl(changes.url);
    onChange(event, changes);
  }

  function onUpdateFiles(files) {
    onChange({}, { placeholderFile: files[0] });
  }

  useEffect(() => {
    onChangeUrlOrId({}, { value: debouncedUrlOrId });
  }, [debouncedUrlOrId]);

  const previewEmbedParams = calculateEmbedParams(videoId, source, url);
  const shouldShowPreview =
    (_get(previewEmbedParams, 'id') && _get(previewEmbedParams, 'source')) ||
    _get(previewEmbedParams, 'url');

  return (
    <div className="video-player-editor">
      <VideoPlayerSources
        videoSourceOptions={videoSourceOptions}
        selectedSource={source}
        onChange={onChange}
      />
      <Form.Input
        placeholder="https://player.vimeo.com/video/948920753"
        label={urlLabel}
        value={urlOrId}
        onChange={(_, { value }) => setUrlOrId(value)}
        error={!isValidUrl ? { content: urlErrorMessage } : undefined}
      />
      <div className="video-player-placeholder-file">
        <strong className="field-label">
          Placeholder Image
          <HelpTooltip
            title="Placeholder Image"
            content="Optionally add an image to show as placeholder for the video."
          />
        </strong>
        <FileSelection
          onUpdateFiles={onUpdateFiles}
          maxAllowedImages={1}
          files={placeholderFile ? [placeholderFile] : []}
        />
      </div>
      <div className="video-player-preview">
        {shouldShowPreview && (
          <>
            <strong className="field-label">Video Preview</strong>
            <Embed placeholder={_get(placeholderFile, 'file')} {...previewEmbedParams} />
          </>
        )}
      </div>
    </div>
  );
}

VideoPlayerEditor.propTypes = propTypes;
VideoPlayerEditor.defaultProps = defaultProps;
